import React, { useState, useEffect } from "react";
import {
  Modal,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  TextInput,
} from "react-native";
import StyledText from "../../common/ui/styled-text";
import { X, DollarSign } from "lucide-react-native";
import CurrencyInput from "../../ui/currency-input";
import { formatPhoneNumber } from "react-phone-number-input";

// Constants for charge validation
const MIN_CHARGE_AMOUNT_CENTS = 1; // $0.01 minimum
const MAX_CHARGE_AMOUNT_CENTS = 100000; // $1,000.00 maximum

// Helper function to format currency display
const formatCurrencyDisplay = (value: number) => {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface ChargeModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (amount: number, reason: string) => void;
  isLoading?: boolean;
  callerName?: string;
  phone: string;
}

const ChargeModal: React.FC<ChargeModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isLoading = false,
  callerName,
  phone,
}) => {
  const [amountInCents, setAmountInCents] = useState(0);
  const [reason, setReason] = useState("");

  const handleSubmit = () => {
    onSubmit(amountInCents, reason);
  };

  const isSubmitDisabled =
    amountInCents === 0 ||
    amountInCents < MIN_CHARGE_AMOUNT_CENTS ||
    amountInCents > MAX_CHARGE_AMOUNT_CENTS ||
    isLoading;

  const resetForm = () => {
    setAmountInCents(0);
    setReason("");
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  // Get the display name for the title
  const getDisplayName = () => {
    if (callerName) {
      return callerName;
    }
    return formatPhoneNumber(phone) || phone;
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="none"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View className="flex-1 bg-black/50 justify-center items-center">
          <TouchableWithoutFeedback>
            <View className="bg-white rounded-xl p-6 w-[90%] max-w-md">
              {/* Header */}
              <View className="flex-row justify-between items-center mb-4">
                <StyledText className="text-xl font-semibold text-gray-900">
                  Charge {getDisplayName()}
                </StyledText>
                <TouchableOpacity
                  onPress={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} color="#6b7280" />
                </TouchableOpacity>
              </View>

              {/* Amount Input */}
              <View className="mb-6">
                <StyledText className="text-sm font-medium text-gray-700 mb-2">
                  Charge amount:
                </StyledText>
                <CurrencyInput
                  value={amountInCents}
                  onChangeValue={setAmountInCents}
                />
                {/* <View className="relative">
                  <DollarSign
                    size={18}
                    color="#6b7280"
                    className="absolute left-3 top-4 z-10"
                  />
                  <TextInput
                    value={amount}
                    onChangeText={setAmount}
                    placeholder="0.00"
                    keyboardType="numeric"
                    className={`pl-10 pr-3 py-3 border rounded-lg text-base ${
                      amount &&
                      (parseFloat(amount) < MIN_CHARGE_AMOUNT ||
                        parseFloat(amount) > MAX_CHARGE_AMOUNT ||
                        !isValidDecimalFormat(amount))
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholderTextColor="#9ca3af"
                  />
                </View> */}
                {/* {amountInCents &&
                  (parseFloat(amountInCents) < MIN_CHARGE_AMOUNT ||
                    parseFloat(amountInCents) > MAX_CHARGE_AMOUNT ||
                    !isValidDecimalFormat(amountInCents)) && (
                    <StyledText className="text-xs text-red-600 mt-1">
                      {!isValidDecimalFormat(amountInCents)
                        ? "Amount must have exactly 2 decimal places (e.g., 5.00)"
                        : parseFloat(amountInCents) < MIN_CHARGE_AMOUNT
                        ? `Minimum charge amount is ${formatCurrencyDisplay(
                            MIN_CHARGE_AMOUNT
                          )}`
                        : `Maximum charge amount is ${formatCurrencyDisplay(
                            MAX_CHARGE_AMOUNT
                          )}`}
                    </StyledText>
                  )} */}
              </View>

              {/* Reason Input */}
              <View className="mb-6">
                <StyledText className="text-sm font-medium text-gray-700 mb-2">
                  Comment
                </StyledText>
                <TextInput
                  value={reason}
                  onChangeText={setReason}
                  multiline
                  numberOfLines={3}
                  className="p-3 border border-gray-300 rounded-lg text-base min-h-[80px]"
                  placeholderTextColor="#9ca3af"
                  textAlignVertical="top"
                />
              </View>

              {/* Action Buttons */}
              <View className="flex-row justify-end space-x-3 pt-4 border-t border-gray-200">
                <TouchableOpacity
                  onPress={onClose}
                  className="px-4 py-2 rounded-lg border border-gray-300"
                >
                  <StyledText className="text-gray-700 font-medium">
                    Cancel
                  </StyledText>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleSubmit}
                  disabled={isSubmitDisabled}
                  className={`px-4 py-2 rounded-lg ${
                    isSubmitDisabled
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  <StyledText className="text-white font-medium">
                    {isLoading
                      ? "Processing..."
                      : `Charge ${formatCurrencyDisplay(amountInCents / 100)}`}
                  </StyledText>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default ChargeModal;
